import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ApiClientError } from '../../../services/apiClient';
import { aprovarEmpresa, detalharAprovacaoEmpresa, listarAprovacoesEmpresas, rejeitarEmpresa } from '../../../services/empresaApprovalApi';
import { EmpresaAnaliseStatus, EmpresaAprovacaoDetalhe, EmpresaAprovacaoFilters, EmpresaAprovacaoItem, EmpresaAprovacaoQuery } from '../../../types/empresaApproval';
import ApprovalDetailModal from './ApprovalDetailModal';
import ApprovalFilters from './ApprovalFilters';
import ApprovalTable from './ApprovalTable';
import DecisionModal from './DecisionModal';

const PAGE_SIZE = 10;
const EMPTY_FILTERS: EmpresaAprovacaoFilters = { cnpj: '', razaoSocial: '', tipoEmpresa: '', papel: '', uf: '', cadastradaDe: '', cadastradaAte: '' };

function errorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.status === 403) return 'Seu perfil não possui permissão para analisar empresas.';
    if (error.code === 'EMPRESA_APROVACAO_JA_DECIDIDA') return 'Esta empresa já foi analisada por outro usuário. Atualize a fila.';
    return error.message;
  }
  return 'Não foi possível concluir a operação.';
}

function toQuery(filters: EmpresaAprovacaoFilters): Omit<EmpresaAprovacaoQuery, 'page' | 'pageSize' | 'statusAnalise'> {
  return {
    cnpj: filters.cnpj || undefined,
    razaoSocial: filters.razaoSocial.trim() || undefined,
    tipoEmpresa: filters.tipoEmpresa || undefined,
    papel: filters.papel || undefined,
    uf: filters.uf || undefined,
    cadastradaDe: filters.cadastradaDe || undefined,
    cadastradaAte: filters.cadastradaAte || undefined,
  };
}

const EmpresaApprovalPage: React.FC = () => {
  const { accessToken, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedEmpresaId = searchParams.get('empresaId');
  const canApprove = hasPermission('empresas.aprovar');
  const [view, setView] = useState<Exclude<EmpresaAnaliseStatus, 'todos'>>('pendente');
  const [draftFilters, setDraftFilters] = useState<EmpresaAprovacaoFilters>(EMPTY_FILTERS);
  const [query, setQuery] = useState<Omit<EmpresaAprovacaoQuery, 'page' | 'pageSize' | 'statusAnalise'>>({});
  const [items, setItems] = useState<EmpresaAprovacaoItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selected, setSelected] = useState<EmpresaAprovacaoDetalhe | null>(null);
  const [decision, setDecision] = useState<'aprovar' | 'rejeitar' | null>(null);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const load = useCallback(async () => {
    if (!accessToken || !canApprove) return;
    setLoading(true); setError(null);
    try {
      const result = await listarAprovacoesEmpresas(accessToken, { page, pageSize: PAGE_SIZE, statusAnalise: view, ...query });
      setItems(result.items); setTotal(result.total);
    } catch (loadError: unknown) { setError(errorMessage(loadError)); }
    finally { setLoading(false); }
  }, [accessToken, canApprove, page, query, view]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!accessToken || !canApprove || !linkedEmpresaId) return;
    let active = true;
    setDetailLoading(true);
    detalharAprovacaoEmpresa(accessToken, linkedEmpresaId)
      .then((detail) => { if (active) setSelected(detail); })
      .catch((detailError: unknown) => { if (active) setError(errorMessage(detailError)); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [accessToken, canApprove, linkedEmpresaId]);

  const applyFilters = () => {
    if (draftFilters.cnpj && draftFilters.cnpj.length !== 14) { setError('Informe os 14 dígitos do CNPJ ou deixe o filtro vazio.'); return; }
    if (draftFilters.cadastradaDe && draftFilters.cadastradaAte && draftFilters.cadastradaDe > draftFilters.cadastradaAte) { setError('A data inicial não pode ser posterior à data final.'); return; }
    setError(null); setPage(1); setQuery(toQuery(draftFilters));
  };

  const openDetails = async (empresa: EmpresaAprovacaoItem) => {
    if (!accessToken) return;
    setDetailLoading(true); setError(null);
    try { setSelected(await detalharAprovacaoEmpresa(accessToken, empresa.id)); }
    catch (detailError: unknown) { setError(errorMessage(detailError)); }
    finally { setDetailLoading(false); }
  };

  const confirmDecision = async (parecer: string) => {
    if (!accessToken || !selected || !decision) return;
    setSaving(true); setDecisionError(null);
    try {
      const updated = decision === 'aprovar' ? await aprovarEmpresa(accessToken, selected.id, parecer) : await rejeitarEmpresa(accessToken, selected.id, parecer);
      setSelected(updated); setDecision(null); setSuccess(`Empresa ${decision === 'aprovar' ? 'aprovada' : 'rejeitada'} com sucesso.`); await load();
    } catch (saveError: unknown) { setDecisionError(errorMessage(saveError)); }
    finally { setSaving(false); }
  };

  if (!accessToken) return <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">A central real exige autenticação na API local.</div>;
  if (!canApprove) return <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-900">Seu perfil não possui a permissão <code>empresas.aprovar</code>.</div>;

  return <div className="space-y-5">
    <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><div className="flex items-center gap-3"><div className="rounded-xl bg-blue-100 p-3 text-blue-700"><Building2 size={24} /></div><div><h1 className="text-2xl font-bold text-gray-900">Aprovação de Empresas</h1><p className="text-sm text-gray-500">Análise cadastral persistente e auditável. A validação documental permanece independente.</p></div></div><button type="button" onClick={() => void load()} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"><RefreshCw size={16} />Atualizar</button></header>
    <nav aria-label="Visão das análises" className="flex flex-wrap gap-2">{([{ value: 'pendente', label: 'Fila pendente' }, { value: 'aprovado', label: 'Histórico aprovado' }, { value: 'rejeitado', label: 'Histórico rejeitado' }] as const).map((tab) => <button key={tab.value} type="button" aria-pressed={view === tab.value} onClick={() => { setView(tab.value); setPage(1); setSuccess(null); }} className={`rounded-lg px-4 py-2 text-sm font-medium ${view === tab.value ? 'bg-blue-700 text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>{tab.label}</button>)}</nav>
    {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
    {success && <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{success}</div>}
    <ApprovalFilters value={draftFilters} disabled={loading} onChange={setDraftFilters} onSubmit={applyFilters} onClear={() => { setDraftFilters(EMPTY_FILTERS); setQuery({}); setPage(1); setError(null); }} />
    <div className="flex items-center justify-between text-sm text-gray-500"><span>{loading || detailLoading ? 'Carregando dados reais...' : `${total} empresa(s) encontrada(s)`}</span><span>Página {page} de {totalPages}</span></div>
    {!loading && <ApprovalTable empresas={items} onDetails={(empresa) => void openDetails(empresa)} />}
    <div className="flex justify-end gap-2"><button type="button" disabled={loading || page <= 1} onClick={() => setPage((current) => current - 1)} className="rounded-lg border px-4 py-2 text-sm disabled:opacity-40">Anterior</button><button type="button" disabled={loading || page >= totalPages} onClick={() => setPage((current) => current + 1)} className="rounded-lg border px-4 py-2 text-sm disabled:opacity-40">Próxima</button></div>
    {selected && <ApprovalDetailModal empresa={selected} onClose={() => { if (!saving) setSelected(null); }} onDecision={(next) => { setDecisionError(null); setDecision(next); }} onOpenManagement={() => navigate(`/backoffice/gestao-empresas?search=${encodeURIComponent(selected.cnpj)}`)} />}
    {selected && decision && <DecisionModal empresaNome={selected.razaoSocial} decisao={decision} saving={saving} apiError={decisionError} onConfirm={(parecer) => void confirmDecision(parecer)} onCancel={() => { if (!saving) { setDecision(null); setDecisionError(null); } }} />}
  </div>;
};

export default EmpresaApprovalPage;
